"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { ArrowUpRight } from "lucide-react";
import AnimatedFooterWord from "@/components/site/animated-footer-word";
import { SiGithub, SiGmail, SiInstagram, SiLinkedin } from "react-icons/si";

const footerNavigation = [
	{
		title: "Explore",
		links: [
			{ href: "/", label: "Home" },
			{ href: "/about", label: "About" },
			{ href: "/projects", label: "Projects" },
			{ href: "/blog", label: "Blog" },
		],
	},
	{
		title: "Highlights",
		links: [
			{ href: "/certifications", label: "Certifications" },
			{ href: "/projects/side-projects", label: "Side Projects" },
			{ href: "/contact", label: "Contact" },
		],
	},
];

const socialLinks = [
	{
		href: "mailto:mhmd.nurfat@gmail.com",
		label: "Email",
		icon: SiGmail,
	},
	{
		href: "https://github.com/mhmdnurf",
		label: "GitHub",
		icon: SiGithub,
	},
	{
		href: "https://www.linkedin.com/in/mhmd-nurfat/",
		label: "LinkedIn",
		icon: SiLinkedin,
	},
	{
		href: "https://instagram.com/mhmd.zaka",
		label: "Instagram",
		icon: SiInstagram,
	},
];

export default function SiteFooter() {
	const pathname = usePathname();
	const showHomeCta = pathname === "/";

	return (
		<footer className="relative overflow-hidden border-t border-white/10 bg-[#080a0f]">
			<div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/25 to-transparent" />
			<div className="pointer-events-none absolute -left-28 bottom-0 h-72 w-72 rounded-full bg-white/[0.04] blur-3xl" />
			<div className="pointer-events-none absolute -right-24 top-10 h-72 w-72 rounded-full bg-[#94a3b8]/[0.06] blur-3xl" />

			<div className="relative mx-auto w-full max-w-6xl px-4 pt-10 sm:px-6 sm:pt-12">
				{showHomeCta ? (
					<div className="grid gap-8 border-b border-white/10 pb-9 lg:grid-cols-[1.3fr_0.7fr] lg:items-end">
						<div className="max-w-2xl">
							<h2 className="font-rethink mt-3 text-3xl font-semibold leading-tight text-white sm:text-4xl">
								Are we chasing the same <AnimatedFooterWord />
							</h2>
							<p className="mt-4 max-w-xl text-sm leading-6 text-zinc-400 sm:text-base">
								Let’s explore good software together, or maybe work on something
								meaningful with your team.
							</p>
						</div>

						<div className="flex flex-col gap-3 sm:flex-row lg:justify-end">
							<Link
								href="/contact"
								className="inline-flex items-center justify-center gap-2 rounded-[0.65rem] bg-zinc-100 px-4 py-2.5 text-sm font-semibold text-zinc-950 transition hover:bg-white"
							>
								Let&apos;s Talk
								<ArrowUpRight className="h-4 w-4" strokeWidth={2} />
							</Link>
							<Link
								href="/projects"
								className="inline-flex items-center justify-center gap-2 rounded-[0.65rem] border border-white/10 bg-white/[0.03] px-4 py-2.5 text-sm font-semibold text-zinc-100 transition hover:border-white/20 hover:bg-white/[0.06]"
							>
								View Projects
							</Link>
						</div>
					</div>
				) : null}

				<div className="grid gap-8 py-8 md:grid-cols-[1.25fr_1fr] lg:grid-cols-[1.4fr_1fr_1fr]">
					<div>
						<Link
							href="/"
							className="font-rethink inline-flex text-xl font-semibold text-white transition hover:text-zinc-200"
						>
							Muhammad Nurfatkhur Rahman
						</Link>
						<p className="mt-3 max-w-sm text-sm leading-6 text-zinc-500">
							Building applications, exploring systems, and sharing what I
							learn.
						</p>
						<div
							className="mt-6 flex flex-wrap items-center gap-3"
							aria-label="Social links"
						>
							{socialLinks.map((item) => {
								const Icon = item.icon;
								const isExternal = item.href.startsWith("http");

								return (
									<a
										key={item.href}
										href={item.href}
										target={isExternal ? "_blank" : undefined}
										rel={isExternal ? "noopener noreferrer" : undefined}
										aria-label={item.label}
										className="inline-flex h-11 w-11 cursor-ne-resize cursor-apple-ne-resize items-center justify-center rounded-[0.75rem] border border-white/10 bg-white/[0.03] text-zinc-400 transition hover:-translate-y-0.5 hover:border-white/20 hover:bg-white/[0.06] hover:text-white"
									>
										<Icon className="h-5 w-5" />
									</a>
								);
							})}
						</div>
					</div>

					{footerNavigation.map((group) => (
						<nav
							key={group.title}
							aria-label={`${group.title} footer navigation`}
						>
							<h3 className="text-sm font-semibold text-zinc-200">
								{group.title}
							</h3>
							<ul className="mt-4 space-y-3">
								{group.links.map((item) => (
									<li key={item.href}>
										<Link
											href={item.href}
											className="text-sm text-zinc-500 transition hover:text-white"
										>
											{item.label}
										</Link>
									</li>
								))}
							</ul>
						</nav>
					))}
				</div>

				<div className="relative flex h-[100px] items-center justify-center px-6">
					<div className="pointer-events-none absolute inset-x-6 -top-px h-px bg-[linear-gradient(90deg,transparent_0%,rgba(148,163,184,0.06)_12%,rgba(203,213,225,0.15)_30%,rgba(248,250,252,0.28)_50%,rgba(203,213,225,0.15)_70%,rgba(148,163,184,0.06)_88%,transparent_100%)] sm:inset-x-8" />
					<div className="pointer-events-none absolute left-1/2 -top-px h-px w-[34%] -translate-x-1/2 bg-[linear-gradient(90deg,transparent,rgba(248,250,252,0.36),transparent)] blur-[0.5px]" />
					<p className="text-center text-sm leading-none text-[#94a3b8]">
						© {new Date().getFullYear()} Muhammad Nurfatkhur Rahman. All rights
						reserved.
					</p>
				</div>
			</div>
		</footer>
	);
}
